import ResponsiveContainer from './ResponsiveContainer.jsx';
import { ChartTooltip, useChartHover } from './ChartTooltip.jsx';
import { bandScale, linearScale, niceTicks } from './scale.js';
import { SERIES, TRACK, FONT, LABEL, LABEL_STRONG } from './chartColors.js';
import Axis from './Axis.jsx';

// Themed vertical bar chart built on the kit primitives. Accessors all receive
// (datum, index):
//   x        — category label (for the default tooltip + aria)
//   y        — numeric value
//   colorOf  — per-bar fill token (default SERIES.accent); use chartColors only
//   mutedOf  — true → render the bar in the muted TRACK colour (small-N honesty)
//   tooltip  — node shown on hover/focus (default `${x}: ${y}`)
//   xLabel   — optional per-bar category label drawn under the axis
//   strokeOf — per-bar outline token; give this to any bar with a translucent
//              fill, which cannot reach 3:1 on its own (see SOFT_FLOOR)
//   showValues — draw each value above its bar (for at-a-glance histograms)
//   valueText  — formatter for the above-bar value label (default the raw value)
//   yAxis    — draw a quantitative y-axis: nice gridlines, tick labels, baseline
//   yTickText— formatter for a tick label (default the raw tick, e.g. "40%")
//   domainMax— force the top of the scale; pass the SAME value to two charts to
//              make them directly comparable (without it each chart scales to
//              its own max and equal bar heights mean different numbers)
// `height` is the TOTAL svg height; axis/value labels are laid out inside it.
// Colors must come from chartColors (theme-safe vars), never a Tailwind class.
// Bars are focusable (tabIndex 0); the focus ring is the global :focus-visible
// rule in index.css — no per-rect outline needed.
export default function BarChart({
  data = [],
  x = (d) => d.label,
  y = (d) => d.value,
  colorOf,
  mutedOf,
  strokeOf,
  tooltip,
  xLabel,
  showValues = false,
  valueText,
  yAxis = false,
  // T342 — a horizontal reference value drawn across the plot. Optional; the
  // charts that have nothing to compare against pass nothing and get nothing.
  refValue = null,
  refLabel = '',
  yTickText,
  domainMax,
  height = 64,
  ariaLabel,
}) {
  const { hover, show, hide } = useChartHover();
  const dataMax = Math.max(1, ...data.map((d, i) => y(d, i)));
  // T238 — niceTicks and the GRID/AXIS tokens shipped with zero call-sites, so
  // every bar chart on the page was a bare silhouette: magnitude was readable
  // only by hovering, and two charts side by side could not be compared because
  // each scaled to its own max. The scale now ends on a round tick, and
  // `domainMax` lets two charts share one.
  const ticks = yAxis ? niceTicks(domainMax ?? dataMax, 4) : null;
  const max = ticks ? ticks[ticks.length - 1] : (domainMax ?? dataMax);
  // T353 — the y-axis needs headroom too, not just the value labels.
  //
  // `padTop` was `showValues ? 14 : 0`, so a chart with `yAxis` and no
  // `showValues` got zero — and Axis draws its topmost tick label at
  // `yOf(top) + 3`, which is y=3 for a 9px font. Roughly half of that digit
  // sits above the SVG's top edge and is clipped. Visible on the weekly trend
  // card from the moment T334 gave it an axis.
  const padTop = showValues ? 14 : (yAxis ? 8 : 0);
  const padBottom = xLabel ? 16 : 0; // room for the category axis labels
  const padLeft = yAxis ? 26 : 0; // gutter for tick labels

  return (
    <div className="relative">
      <ResponsiveContainer height={height}>
        {(w, h) => {
          const plotW = Math.max(2, w - padLeft);
          const band = bandScale(data.length, plotW, { padding: 0.25 });
          const plotH = Math.max(2, h - padTop - padBottom);
          const scaleY = linearScale(max, plotH);
          const yOf = (v) => padTop + plotH - scaleY(v);
          return (
            <>
              <svg width={w} height={h} role="group" aria-label={ariaLabel} style={{ display: 'block', fontFamily: FONT }}>
                {/* T341 — the shared axis. */}
                <Axis ticks={ticks} yOf={yOf} padLeft={padLeft} width={w} tickText={yTickText} />
                {/* T342 — the reference line, under the bars so it never hides
                    one. Dashed and in the muted label colour: it is a
                    comparison, not another series, and drawing it solid would
                    put a fifth bar on a four-bar chart. */}
                {refValue != null && Number.isFinite(refValue) && refValue > 0 && (
                  <g>
                    <line
                      x1={padLeft}
                      x2={w}
                      y1={yOf(refValue)}
                      y2={yOf(refValue)}
                      stroke={LABEL}
                      strokeWidth="1"
                      strokeDasharray="3 3"
                      shapeRendering="crispEdges"
                    />
                    {refLabel && (
                      <text x={w - 2} y={yOf(refValue) - 3} textAnchor="end" fontSize="9" fill={LABEL}>
                        {refLabel}
                      </text>
                    )}
                  </g>
                )}
                {data.map((d, i) => {
                  const val = y(d, i);
                  const barH = Math.max(2, scaleY(val));
                  const bx = padLeft + band.x(i);
                  const cx = bx + band.bandWidth / 2;
                  const barTop = padTop + (plotH - barH);
                  const fill = mutedOf?.(d, i) ? TRACK : colorOf ? colorOf(d, i) : SERIES.accent;
                  const content = tooltip ? tooltip(d, i) : `${x(d, i)}: ${val}`;
                  const enter = () => show(cx, barTop, content);
                  return (
                    <g key={i}>
                      {showValues && (
                        <text x={cx} y={barTop - 3} textAnchor="middle" fontSize="10" fill={LABEL_STRONG}>
                          {valueText ? valueText(d, i) : val}
                        </text>
                      )}
                      <rect
                        // T344 — tween the geometry so a range switch shows the
                        // bars moving rather than teleporting to a new chart.
                        className="chart-tween"
                        x={bx}
                        y={barTop}
                        width={band.bandWidth}
                        height={barH}
                        rx={2}
                        fill={fill}
                        stroke={strokeOf?.(d, i)}
                        strokeWidth={strokeOf?.(d, i) ? 1 : undefined}
                        tabIndex={0}
                        role="img"
                        aria-label={typeof content === 'string' ? content : `${x(d, i)}: ${val}`}
                        style={{ cursor: 'default' }}
                        onMouseEnter={enter}
                        onMouseLeave={hide}
                        onFocus={enter}
                        onBlur={hide}
                      />
                      {xLabel && (
                        <text x={cx} y={h - 4} textAnchor="middle" fontSize="10" fill={LABEL}>
                          {xLabel(d, i)}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
              <ChartTooltip hover={hover} width={w} />
            </>
          );
        }}
      </ResponsiveContainer>
    </div>
  );
}
